import { useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { useAppSelector } from "../../../redux/hooks";
import { Button } from "../basic/Button";
import { TextAreaField } from "../../fields/base/TextAreaField";

function safeStringify(value: unknown): string {
  if (value === undefined) {
    return "Error value is undefined";
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    if (typeof value === "object" && value !== null) {
      return "[Circular or non-serializable object]";
    }
    // Objects already returned above, so `value` is a primitive here.
    // oxlint-disable-next-line @typescript-eslint/no-base-to-string
    return typeof value === "symbol" ? value.toString() : String(value);
  }
}

function formatError(error: unknown): string {
  if (error instanceof Error) {
    const errorObj: Record<string, unknown> = {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
    for (const [key, value] of Object.entries(error)) {
      errorObj[key] = value;
    }
    return safeStringify(errorObj);
  }
  return safeStringify(error);
}

export function DevErrorDisplay({ error, userMessage }: { error?: unknown; userMessage?: string }) {
  const { t } = useTranslation(["common"]);
  const isStaffUser = useAppSelector((state) => state.user.is_staff);
  const [copied, setCopied] = useState(false);

  const showErrorDetails = !import.meta.env.PROD || isStaffUser;
  const formattedError = formatError(error);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formattedError);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may fail in some contexts
    }
  };

  return (
    <VStack gap="8px" alignItems="flex-start" width="100%">
      <p className={css({ color: "gray.800", fontSize: "lg", fontWeight: "medium" })}>{userMessage}</p>
      {showErrorDetails && (
        <>
          <HStack gap="8px" alignItems="center">
            <p className={css({ color: "gray.600", fontSize: "sm" })}>
              {t(($) => $.common.devErrorDisplay["Copy this error and include it with your bug report."])}
            </p>
            <Button variant="secondary" size="small" onPress={handleCopy}>
              {copied ? t(($) => $.common.devErrorDisplay["Copied!"]) : t(($) => $.common.devErrorDisplay["Copy"])}
            </Button>
          </HStack>
          <TextAreaField
            aria-label={t(($) => $.common.devErrorDisplay["Error details"])}
            className={css({
              height: "300px",
              overflow: "auto",
            })}
            value={formattedError}
            isReadOnly
          />
        </>
      )}
    </VStack>
  );
}
